import { ArrowUpIcon, Info } from 'components/icons';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from 'components/ui/input-group';
import { ReactNode, Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { cn } from 'utils/cn';
import { isComposing } from 'utils/isComposing';
import type { AiChatRequestRecord } from '../hooks/useAiChatController';
import { PromptSelectHandler, usePlaceholderFill } from '../hooks/usePlaceholderFill';
import AiChatDisclaimer from './AiChatDisclaimer';
import PlaceholderFillBar from './PlaceholderFillBar';
import type { SlotRecord } from './PlaceholderAutocomplete';

const REQUEST_RECORD_TYPE: Record<SlotRecord['entityType'], AiChatRequestRecord['type']> = {
    PARTY: 'contact',
    PARTY_GROUP: 'group',
    MATTER: 'case',
};

/** What the conversation above the composer may do with it, through the option-pick context. */
export interface ChatComposerHandle {
    focus: () => void;
    /** Replaces the text, ending any template being filled and forgetting the prompt it came from. */
    setDraft: (text: string) => void;
    /** Says `text` through the conversation's status region. */
    announce: (text: string) => void;
    /** Shows `text` as the placeholder; null gives the surface's own back. */
    setPlaceholder: (text: string | null) => void;
    /** Puts a ready-made prompt in the composer, unsent, its slots to fill. */
    selectPrompt: PromptSelectHandler;
}

/** Keeps `--ai-composer-height` on the document at `element`'s height while `active`, for the phone's picker sheet. */
function useComposerHeightVariable(element: HTMLElement | null, active: boolean) {
    useEffect(() => {
        if (!element || !active) return;
        const root = document.documentElement;
        const observer = new ResizeObserver(() =>
            root.style.setProperty('--ai-composer-height', `${element.offsetHeight}px`)
        );
        observer.observe(element);
        return () => {
            observer.disconnect();
            root.style.removeProperty('--ai-composer-height');
        };
    }, [element, active]);
}

interface ChatComposerProps {
    ref?: Ref<ChatComposerHandle>;
    variant: 'panel' | 'page' | 'phone';
    /** The page with its history hidden: its column, and the composer with it, widens. */
    wide?: boolean;
    value: string;
    onValueChange: (value: string) => void;
    placeholder: string;
    isStreaming: boolean;
    /**
     * The composer has emptied itself already. `promptId` names the ready-made prompt the text came from, `records`
     * the records picked into its slots that the text still names.
     */
    onSend: (text: string, promptId: string | null, records: AiChatRequestRecord[]) => void;
    onStop: () => void;
    /** Shown in place of the field while set: the panel's seeded question, as it will read once sent. */
    preview?: ReactNode;
}

/**
 * The one composer of the panel, the page and the phone (AI Element Prompt Input on the kit's InputGroup): Enter
 * sends, Shift+Enter breaks the line, and Send and Stop share one slot as an icon swap. A ready-made prompt's slots
 * fill inline; a send with one still empty is held and says which. It also mounts the conversation's one status
 * region, empty until something is announced, and the disclaimer under itself.
 */
export default function ChatComposer({
    ref,
    variant,
    wide = false,
    value,
    onValueChange,
    placeholder,
    isStreaming,
    onSend,
    onStop,
    preview,
}: ChatComposerProps) {
    const intl = useIntl();
    const isPhone = variant === 'phone';
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const slotsRef = useRef<HTMLDivElement>(null);
    const [placeholderOverride, setPlaceholderOverride] = useState<string | null>(null);
    const [announcement, setAnnouncement] = useState<{ id: number; text: string } | null>(null);
    // The ready-made prompt the text came from, until it is sent or the composer is emptied.
    const [promptId, setPromptId] = useState<string | null>(null);
    // The text a send was refused for; the missing slot is named only after such an attempt, and only while it stands.
    const [attemptedText, setAttemptedText] = useState<string | null>(null);
    const [box, setBox] = useState<HTMLDivElement | null>(null);
    const [field, setField] = useState<HTMLDivElement | null>(null);

    const onValueChangeRef = useRef(onValueChange);
    onValueChangeRef.current = onValueChange;
    const setComposerValue = useCallback((_name: 'message', next: string) => onValueChangeRef.current(next), []);
    const focus = useCallback(() => (textareaRef.current ?? slotsRef.current)?.focus(), []);
    const fill = usePlaceholderFill({ setValue: setComposerValue, setFocus: focus });
    const { placeholderSession: session } = fill;

    const change = (next: string) => {
        onValueChange(next);
        if (!next) {
            setPromptId(null);
            fill.clearSession();
        }
    };

    useImperativeHandle(ref, () => ({
        focus,
        setDraft: (text) => {
            fill.clearSession();
            setPromptId(null);
            onValueChange(text);
        },
        announce: (text) => setAnnouncement((previous) => ({ id: (previous?.id ?? 0) + 1, text })),
        setPlaceholder: setPlaceholderOverride,
        selectPrompt: (text, optionsMap, config, nextPromptId) => {
            setPromptId(nextPromptId ?? null);
            setAttemptedText(null);
            fill.handlePromptSelect(text, optionsMap, config);
        },
    }));

    const unfilled = fill.findUnfilledPlaceholder(value);
    const filledText = (fill.getPlaceholderFilledText(value) ?? value).trim();

    const submit = () => {
        if (isStreaming || !filledText) return;
        if (fill.findUnfilledPlaceholder(filledText)) {
            setAttemptedText(value);
            return;
        }
        const sentPromptId = promptId;
        // The backend caps a label at 200 UTF-16 units; a cut through an emoji drops its orphaned high surrogate.
        const records = fill.getPickedRecords(filledText).map(({ entityType, id, label }) => ({
            type: REQUEST_RECORD_TYPE[entityType],
            id: String(id),
            label: label.slice(0, 200).replace(/[\uD800-\uDBFF]$/, ''),
        }));
        fill.clearSession();
        setPromptId(null);
        setAttemptedText(null);
        onValueChange('');
        onSend(filledText, sentPromptId, records);
    };

    const currentSlot = session?.placeholders[session.currentIndex];
    const isPickingRecord = isPhone && currentSlot != null && session?.placeholderInputTypes?.[currentSlot] != null;
    // The phone's picker sheet sits on the composer, which keeps its own height in a variable for it.
    useComposerHeightVariable(box, isPickingRecord);

    const hasText = filledText !== '' && !unfilled;
    const missingSlot = attemptedText === value && unfilled ? unfilled.slice(1, -1) : null;

    return (
        <div
            ref={setBox}
            className={cn(
                'bg-card',
                isPhone && 'border-divider-soft border-t px-4 pt-2.5 pb-4',
                isPhone && isPickingRecord && 'border-t-0',
                variant === 'panel' && 'px-4 pt-2 pb-2.5',
                variant === 'page' && 'bg-transparent'
            )}
        >
            <div
                ref={fill.composerRef}
                className={cn(
                    'relative mx-auto transition-[max-width] duration-0',
                    variant === 'page' &&
                        (wide
                            ? 'max-w-[1040px] px-4 delay-(--resize-dur) motion-reduce:delay-(--duration-quick)'
                            : 'max-w-[720px] px-4')
                )}
            >
                <InputGroup
                    ref={setField}
                    // A slot's popup opens above this box, clear of the prompt being filled.
                    data-ai-composer=""
                    className={cn(
                        'border-input bg-card focus-within:border-ring h-auto flex-col items-stretch gap-1.5 ps-3.5 pe-1.5 pt-2.5 pb-1.5 shadow-none transition-[border-color] duration-150 ease-out has-[[data-slot=input-group-control]:focus-visible]:ring-0',
                        isPhone ? 'rounded-[28px]' : 'rounded-[22px]'
                    )}
                >
                    {session ? (
                        <PlaceholderFillBar
                            ref={slotsRef}
                            session={session}
                            asSheet={isPhone}
                            className={isPhone ? 'text-base' : 'text-body1'}
                            onChange={fill.handlePlaceholderChange}
                            onNext={fill.handlePlaceholderNext}
                            onOptionSelect={fill.handlePlaceholderOptionSelect}
                            onRecordSelect={fill.handlePlaceholderRecordSelect}
                            onDismiss={fill.handlePlaceholderDismiss}
                            onSubmit={submit}
                        />
                    ) : (
                        (preview ?? (
                            <InputGroupTextarea
                                ref={textareaRef}
                                value={value}
                                rows={1}
                                // Read-only rather than disabled while an answer streams, so the field keeps the focus.
                                readOnly={isStreaming}
                                aria-disabled={isStreaming || undefined}
                                // A stable name: the placeholder changes while a choice waits.
                                aria-label={intl.formatMessage({ id: 'ai-chat.floating.fab-label' })}
                                placeholder={placeholderOverride ?? placeholder}
                                onChange={(event) => change(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' && !event.shiftKey && !isComposing(event)) {
                                        event.preventDefault();
                                        submit();
                                    }
                                }}
                                className={cn(
                                    'text-foreground placeholder:text-muted-foreground max-h-32 min-h-6 p-0 pe-2 aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
                                    isPhone ? 'text-base' : 'text-body1'
                                )}
                            />
                        ))
                    )}
                    <InputGroupAddon align="block-end" className="cursor-default gap-1.5 p-0">
                        {missingSlot ? (
                            <span role="alert" className="text-caption text-warning-ink inline-flex items-center gap-1">
                                <Info className="size-3.5" />
                                {intl.formatMessage(
                                    { id: 'ai-chat.input.choose-before-sending' },
                                    { type: missingSlot }
                                )}
                            </span>
                        ) : null}
                        <span className="flex-1" />
                        <InputGroupButton
                            data-state={isStreaming ? 'b' : 'a'}
                            aria-label={intl.formatMessage({ id: isStreaming ? 'ai-chat.stop' : 'ai-chat.send' })}
                            // A held send stays pressable, so the press can say which slot is missing.
                            aria-disabled={!isStreaming && unfilled != null ? true : undefined}
                            disabled={!isStreaming && !filledText}
                            onClick={isStreaming ? onStop : submit}
                            className={cn(
                                // One cell as big as the button, so Stop's track is a circle round the square.
                                't-icon-swap grid-cols-1 grid-rows-1 place-items-center rounded-full p-0 disabled:opacity-100',
                                isPhone ? 'size-11' : 'size-8',
                                isStreaming
                                    ? 'bg-card text-primary hover:bg-card'
                                    : hasText
                                      ? 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
                                      : 'bg-muted text-muted-foreground hover:bg-muted'
                            )}
                        >
                            <span className="t-icon" data-icon="a">
                                <ArrowUpIcon className="size-[18px]" />
                            </span>
                            <span
                                className="t-icon relative grid size-full place-items-center"
                                data-icon="b"
                                aria-hidden
                            >
                                <span
                                    className={cn(
                                        'border-border border-t-primary absolute inset-0 rounded-full border-2',
                                        isStreaming && 'motion-safe:animate-spin motion-reduce:animate-pulse'
                                    )}
                                />
                                <span className="bg-primary size-2.5 rounded-[2px]" />
                            </span>
                        </InputGroupButton>
                    </InputGroupAddon>
                </InputGroup>
                <p role="status" className="sr-only">
                    {announcement ? <span key={announcement.id}>{announcement.text}</span> : null}
                </p>
                <AiChatDisclaimer composer={field} />
            </div>
        </div>
    );
}
